<template>
  <div>
    <label class="heading-l" id="meetingTypeHeading">{{ $t('Select meeting details') }}</label>
    <div class="flex-wrap">
      <fieldset aria-labelledby="meetingTypeHeading" aria-describedby="vmNotAvailable brNotAvailable">
        <div class="small-form small-form--fixed">
          <template v-if="subUnits && groupedSubUnits">
            <div
              v-for="(subUnit, index) in groupedSubUnits"
              :key="index"
              class="d-flex align-items-center radio-button-margin"
            >
              <input
                class="custom-radio"
                type="radio"
                name="meetingType"
                v-model="meetingType"
                :id="subUnit[0].branch_type"
                :value="subUnit[0].branch_type"
                :disabled="getTimesLoading"
              />
              <label class="custom-radio-label" :for="subUnit[0].branch_type">{{
                $t(branchTypeNames(subUnit[0].branch_type))
              }}</label>
            </div>
          </template>
        </div>
        <div class="missing-branch-container" v-if="bookingErrors.includes('branchType')">
          <p class="input-error" v-if="bookingErrors.includes('branchType')">{{ $t('messages.place_error') }}</p>
        </div>
        <div
          v-if="
            subUnits &&
            (!Object.keys(groupedSubUnits).includes('video') || !Object.keys(groupedSubUnits).includes('regular'))
          "
          class="missing-branch-container"
        >
          <p
            v-if="subUnits && !Object.keys(groupedSubUnits).includes('video')"
            :aria-hidden="subUnits && Object.keys(groupedSubUnits).includes('video')"
            class="body-small missing-branch-text"
            id="vmNotAvailable"
            >{{ $t('VM not available') }}</p
          >
          <p
            v-else-if="subUnits && !Object.keys(groupedSubUnits).includes('regular')"
            :aria-hidden="subUnits && Object.keys(groupedSubUnits).includes('regular')"
            class="body-small missing-branch-text"
            id="brNotAvailable"
            >{{ $t('BR meeting not available') }}</p
          >
        </div>
      </fieldset>
    </div>
    <div v-if="branchType">
      <label class="heading-3" id="meetInHeading" v-if="branchType && branchType == 'regular'">{{
        $t('Select the branch')
      }}</label>
      <!-- Branch select & datepicker -->
      <div class="small-form" v-if="branchType && branchType == 'regular'" id="branchSelect">
        <select class="dropdown" v-model="branch" aria-labeledby="meetInHeading">
          <option disabled selected value="">{{ $t('Please select') }}</option>
          <option
            v-for="(subUnit, index) in groupedSubUnits[branchType]"
            :key="index"
            :value="subUnit"
            :disabled="getTimesLoading"
            :aria-label="subUnit.wca ? `${subUnit.name} ${$t('wheelchair_accessible')}` : subUnit.name"
            >{{ subUnit.wca ? `${subUnit.name} &#xf193;` : subUnit.name }}</option
          >
        </select>
        <p class="input-error" v-if="bookingErrors.includes('branch')">{{ $t('messages.choose_branch') }}</p>
      </div>
      <div class="w-100 helptext-margin-bottom" :class="branchType !== 'regular' ? 'helptext-margin-top' : ''">
        <HelpText
          v-if="helpText && $i18n.te(`messages.${helpTextType}`) && $i18n.t(`messages.${helpTextType}`) !== ''"
          :key="helpText"
          :textType="helpTextType"
        />
      </div>
      <label class="heading-3" for="datepickerInput" v-if="branchType">{{ $t('Select the date') }}</label>
      <div class="small-form" tabindex="-1" ref="datepickerContainer" id="datepickerContainer">
        <VDatePicker
          v-model="datePickerValue"
          :min-date="disabledDates.to"
          :max-date="disabledDates.from"
          :disabled-dates="disabledDates.dates"
          :locale="calendarLang($i18n.locale)"
          :first-day-of-week="2"
          :popover="null"
          @popover-will-show="handlePopoverWillShow"
          @popover-did-show="handlePopoverDidShow"
          is-required
          ref="datepicker"
        >
          <template #default="{ inputEvents, togglePopover }">
            <input
              aria-labelledby="datepickerInput"
              name="datepickerInput"
              id="datepickerInput"
              @click="togglePopover"
              @keyup.enter="togglePopover"
              @keyup.space="togglePopover"
              class="input-container__input datepicker-input"
              :disabled="getAvailableDatesLoading || selectedBranch == ''"
              v-on="inputEvents"
              readonly
              :value="datePickerInputText"
            />
          </template>
        </VDatePicker>
        <p class="input-error" v-if="bookingErrors.includes('date')">{{ $t('messages.date_view') }}</p>
      </div>
      <!-- Available Times -->
      <fieldset aria-labeledby="meetingDurationHeading1 meetingDurationLabel">
        <div class="times-container times-container--booking" v-if="times && times.length !== 0">
          <p class="subheading" id="meetingDurationHeading1">{{ $t('Select the time') }}</p>
          <label class="body-small mb-5" v-if="meetingDuration" id="meetingDurationLabel">{{
            $t('Meeting duration: about X minutes', {
              X: meetingDuration,
            })
          }}</label>
          <div class="time-button__container" aria-live="polite">
            <button
              class="time-button"
              @click="selectTime(time)"
              @mouseenter="removeAriaHidden"
              @mouseleave="addAriaHidden"
              v-for="(time, index) in times"
              :key="index"
              :class="{ 'time-button--selected': time == selectedTime }"
              :aria-live="time == selectedTime ? 'polite' : 'off'"
              :ref="'timeButton' + index"
            >
              <img src="../assets/check-icon.svg" alt="selected" aria-label="selected" v-if="time == selectedTime" />
              <span class="sr-only" :aria-label="formatTimeForScreenReader(time.value)"> </span>
              <span :aria-hidden="isHovered ? 'false' : 'true'">{{ time.value }}</span>
            </button>
          </div>
          <p class="input-error" v-if="bookingErrors.includes('time')">{{ $t('messages.time_view') }}</p>
        </div>
      </fieldset>
    </div>
    <!-- Closest branches -->
    <button
      id="closestBranchesButton"
      v-if="closestBranchesButton"
      class="button button--tertiary button--smallbutton--contain-size"
      @click="searchClosestBranches()"
    >
      {{ $t('Find other branches near this location') }}
    </button>
    <div>
      <div class="d-flex justify-content-center" v-if="closestBranchesLoading">
        <div class="spinner spinner-border" role="status">
          <span class="sr-only"></span>
        </div>
      </div>
      <div class="closest-branch-list" v-if="showClosestBranches && !closestBranchesLoading">
        <div
          class="closest-branch-list__item"
          tabindex="0"
          v-for="(branch, index) in closestBranchArray"
          :key="index"
          :class="{ 'closest-branch-list__item--selected': branch == selectedBranch }"
        >
          <div class="closest-branch-list__item__text">
            <p class="closest-branch-list__item__text__name"
              >{{ branch.wca ? `${branch.name} ` : branch.name }}
              <span v-if="branch.wca" :aria-label="$t('wheelchair_accessible')" style="margin-left: 2px">&#xf193;</span>
            </p>
            <p class="closest-branch-list__item__text__time"
              >{{ $t('Earliest date') }}
              <span aria-hidden="true" aria-labelledby="hiddenTime">{{ branch.formattedDate }}</span>
              <span id="timeSlot">{{ customFormatterScreenReader(branch.formattedDate) }}</span>
              <span id="hiddenTime" class="d-none">{{ customFormatterScreenReader(branch.formattedDate) }}</span></p
            >
          </div>

          <button
            aria-live="polite"
            class="closest-branch-list__item__button button button--tertiary button--small button button--contain-size"
            @click="setClosestBranch(branch)"
          >
            <img src="../assets/check-icon-black.svg" alt="selected" v-if="branch == selectedBranch" />
            <span v-else>{{ $t('Show the time') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useBookingStore } from '../store'
import moment from 'moment'
import HelpText from '../components/HelpText'
import 'v-calendar/style.css'
export default {
  name: 'MeetingType',
  components: {
    HelpText,
  },
  data() {
    return {
      type: '',
      appointmentDate: null,
      helpText: false,
      helpTextType: '',
      helpTextType1: 'help_text_video_fp_18',
      closestBranchesButton: false,
      showClosestBranches: false,
      closestBranchesLoading: false,
      popover: {
        visibility: 'focus',
      },
      isHovered: false,
    }
  },
  computed: {
    ...mapState(useBookingStore, [
      'services',
      'groupValue',
      'appCountry',
      'appointmentLang',
      'appointmentType',
      'subUnits',
      'groupedSubUnits',
      'branchType',
      'appCountry',
      'disabledDates',
      'getTimesLoading',
      'selectedBranch',
      'times',
      'appLang',
      'bookingErrors',
      'selectedDate',
      'selectedTime',
      'closestBranchArray',
      'meetingDuration',
      'childrenCount',
      'availableDates',
      'getAvailableDatesLoading',
    ]),

    meetingType: {
      get() {
        return this.branchType
      },
      set(value) {
        this.setBranchType(value)
      },
    },
    lang: {
      get() {
        return this.appointmentLang
      },
      set(value) {
        this.setAppointmentLang(value)
      },
    },
    branch: {
      get() {
        return this.selectedBranch
      },
      set(value) {
        this.setSelectedBranch(value)
      },
    },
    datePickerValue: {
      get() {
        return this.selectedDate
      },
      set(value) {
        this.selectDate(value)
      },
    },
    timesAvailable: {
      get() {
        return this.times.length
      },
    },
    datePickerInputText() {
      if (this.datePickerValue) {
        return this.customFormatter(this.datePickerValue)
      } else {
        return this.getAvailableDatesLoading ? `${this.$i18n.t('Please wait')}...` : this.$i18n.t('Please select')
      }
    },
  },
  watch: {
    meetingType: {
      handler: function () {
        this.setSelectedBranch('') //clear selected branch
        this.setSelectedDate(null) //clear selected date
        this.setSelectedTime('') //clear selected time
        this.unsetHelpText()
        this.resetTimeSlots()
        this.requestDates()
        if (this.selectedBranch && this.selectedBranch.id) {
          this.setHelpText(this.selectedBranch)
        }
        // hide all closest branch stuff on meeting type change
        this.hideClosestBranches()
      },
      deep: true,
    },
    branch: {
      handler: function () {
        if (this.branchType == 'regular') {
          this.requestDates()
          this.unsetHelpText()
          if (this.selectedBranch && this.selectedBranch.id) {
            this.setHelpText(this.selectedBranch)
          }
          //check to hide closest branches
          this.checkToHideClosestBranches()
        } else {
          this.unsetHelpText()
          if (this.selectedBranch && this.selectedBranch.id) {
            this.setHelpText(this.selectedBranch)
          }
        }
        if (this.selectedBranch.closestDate && this.times.length > 0) {
          this.deleteTimes()
        }
        if (!this.selectedBranch.closestDate) {
          this.resetTimeSlots()
        }
        if (this.selectedBranch && this.selectedBranch.id && this.selectDate) {
          this.selectDate(this.selectedDate)
        }
      },
      deep: true,
    },
    timesAvailable: {
      handler: function () {
        if (this.times.length > 0 && this.selectedBranch && this.selectedBranch.closest.length > 0) {
          this.closestBranchesButton = true
        }
      },
      deep: true,
    },
    childrenCount: {
      handler: function () {
        this.setSelectedBranch('') //clear selected branch
        this.setSelectedDate(null) //clear selected date
        this.setSelectedTime('') //clear selected time
        this.unsetHelpText()
        this.resetTimeSlots()
        this.clearMeetingDuration()
        this.requestDates()
        if (this.selectedBranch && this.selectedBranch.id) {
          this.setHelpText(this.selectedBranch)
        }
        // hide all closest branch stuff on meeting type change
        this.hideClosestBranches()
      },
    },
    selectedDate: {
      handler: function () {
        //unset selected time on date change
        this.setSelectedTime('')
      },
    },
  },
  methods: {
    ...mapActions(useBookingStore, [
      'getServices',
      'getTimes',
      'getAvailableDates',
      'setCustomerType',
      'setBranchType',
      'setSelectedBranch',
      'deleteTimes',
      'setErrors',
      'setSelectedDate',
      'setSelectedTime',
      'updateClosestBranchArray',
      'clearMeetingDuration',
    ]),
    requestDates() {
      if (this.branchType == 'video' || this.branchType == 'call') {
        this.setSelectedBranch(this.groupedSubUnits[this.branchType][0])
        this.requestAvailableDates()
      } else if (this.branchType == 'regular' && this.selectedBranch) {
        this.requestAvailableDates()
      }
    },
    requestAvailableDates() {
      const unitId = this.selectedBranch.id
      const groupValue = this.groupValue
      const appointmentId = this.selectedBranch.appointment_type_id
      const lang = this.appLang
      const country = this.appCountry
      const meetingLang = this.appointmentLang
      const childrenCount = this.childrenCount

      const object = { unitId, groupValue, appointmentId, lang, country, meetingLang, childrenCount }
      return this.getAvailableDates(object)
    },
    branchTypeNames(value) {
      if (value == 'video') {
        return 'Video meeting'
      } else if (value == 'regular') {
        return 'Branch meeting'
      } else if (value == 'call') {
        return 'Phone call'
      }
    },
    branchTypeHeadings(value) {
      if (value == 'video') {
        return "Let's have a video meeting"
      } else if (value == 'regular') {
        return 'Select the branch'
      }
    },
    customFormatter(date) {
      return moment(date).format('DD.MM.YYYY')
    },
    calendarLang() {
      switch (this.appLang) {
        case 'lv_LV':
          return 'lv'
        case 'en_US':
          return 'en'
        case 'ru_RU':
          return 'ru'
        case 'et_EE':
          return 'et'
        case 'tt_RU':
          return 'ru'
        case 'lt_LT':
          return 'lt'
      }
    },
    selectDate(date) {
      if (date) {
        this.appointmentDate = moment(date).format('YYYY-MM-DD')
        this.setSelectedDate(this.appointmentDate)
        this.getAppointmentTimes()
      }
    },
    getAppointmentTimes() {
      if (
        this.selectedBranch.id != '' &&
        this.appointmentDate != '' &&
        this.appointmentDate != 'Invalid date' &&
        this.selectedBranch.appointment_type_id
      ) {
        var lang = this.appointmentLang
        var country = this.appCountry
        var unitId = this.selectedBranch.id
        var groupValue = this.groupValue
        var appointmentId = this.selectedBranch.appointment_type_id
        var date = this.appointmentDate
        var meetingLang = this.appointmentLang
        var childrenCount = this.childrenCount
        const object = { unitId, groupValue, appointmentId, date, lang, country, meetingLang, childrenCount }
        this.getTimes(object)
      }
    },
    resetTimeSlots() {
      if (this.times.length) {
        this.deleteTimes()
      }
      this.setSelectedTime('')
      this.appointmentDate = null
      this.datePickerValue = null
    },
    selectTime(value) {
      this.setSelectedTime(value)
    },
    setHelpText(data) {
      this.helpText = data.help_text === false ? false : true
      if (this.helpText) {
        this.helpTextType = data.help_text
      }
    },
    unsetHelpText() {
      this.helpTextType = ''
      this.helpText = false
    },
    searchClosestBranches() {
      if (this.selectedBranch && this.selectedBranch.closest) {
        this.closestBranchesLoading = true
        let closestArray = []
        let matched = {}
        let counter = 0
        //loop closest branches
        this.selectedBranch.closest.forEach((closestBranch) => {
          //find regular branch id that matches closest branch
          matched = this.groupedSubUnits.regular.find((regularBranch) => closestBranch == regularBranch.id)
          //if matched push to array
          if (matched !== undefined) {
            closestArray.push(matched)
          }
        })
        //send times requests to all closest branches
        closestArray.forEach((el) => {
          return new Promise((resolve) => {
            const unitId = el.id
            const groupValue = this.groupValue
            const appointmentId = el.appointment_type_id
            const lang = this.appLang
            const country = this.appCountry
            const meetingLang = this.appointmentLang
            var childrenCount = this.childrenCount

            const object = { unitId, groupValue, appointmentId, lang, country, meetingLang, childrenCount }
            return resolve(this.getAvailableDates(object))
          }).then(() => {
            el.closestDate = this.availableDates.dates[0]
            el.formattedDate = moment(el.closestDate).format('DD.MM.YYYY')
            this.updateClosestBranchArray(closestArray)
            counter++
            // check if all closest branches are looped, and show branch list
            if (counter == closestArray.length) {
              this.showClosestBranches = true
              this.closestBranchesLoading = false
            }
          })
        })
      }
    },
    setClosestBranch(branch) {
      this.branch = branch
      this.setSelectedDate(branch.closestDate)
      this.$refs.datepickerContainer.focus()
    },
    getKeyByValue(object, value) {
      return Object.keys(object).find((key) => object[key] === value)
    },
    hideClosestBranches() {
      this.closestBranchesButton = false
      this.showClosestBranches = false
    },
    checkToHideClosestBranches() {
      //hide closest branch stuff if branch has no closest branches
      if (!this.selectedBranch.closest || this.selectedBranch.closest.length == 0) {
        this.hideClosestBranches()
        return
      }
      //hide closest branch stuff if current branch is not in current closest branch group
      if (this.selectedBranch.closest && this.closestBranchArray.length > 0) {
        let found = false
        this.closestBranchArray.forEach((el) => {
          if (el.id == this.selectedBranch.id) {
            found = true
          }
        })
        found ? null : this.hideClosestBranches()
      }
    },
    async handlePopoverWillShow() {
      const firstAvailableDate = this.datePickerValue ? new Date(this.datePickerValue) : new Date(this.disabledDates.to)
      this.$nextTick(() => {
        this.$refs.datepicker.calendarRef.move(firstAvailableDate)
      })
    },
    async handlePopoverDidShow() {
      // focus on the initial date cell
      const firstAvailableDate = this.datePickerValue ? new Date(this.datePickerValue) : new Date(this.disabledDates.to)
      this.$nextTick(() => {
        if (firstAvailableDate && !this.$refs.datepicker.calendarRef.focusedDay) {
          this.$refs.datepicker.calendarRef.tryFocusDate(firstAvailableDate)
        }
      })
    },
    formatTimeForScreenReader(time) {
      return moment(time, 'HH:mm').format('h:mm A')
    },
    removeAriaHidden() {
      this.isHovered = true
    },
    addAriaHidden() {
      this.isHovered = false
    },
    customFormatterScreenReader(date) {
      const screenReaderDate = moment(date, 'DD.MM.YYYY').format('D MMMM, YYYY')
      return screenReaderDate
    },
  },
}
</script>

<style scoped>
#timeSlot {
  position: absolute;
  right: 0;
  opacity: 0;
  width: 80px;
  height: 20px;
}
</style>
